import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { CprNummerPipe } from '@core/pipes/cprnummer.pipe';
import { LetDatePickerComponent } from '@shared/let-datepicker/let-date-picker.component';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { ScrollPanel } from 'primeng/scrollpanel';
import { OpkraevningUdestaaendeService } from '../opkraevning-udestaaende.service';
import { Dialog } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { RisikoOpkraevning } from '@features/risikoopkraevning/risiko-opkraevning.model';
import { TableModule } from 'primeng/table';
import { TableRowDirective } from '@core/primevue/directives/table-row.directive';
import { Panel } from 'primeng/panel';

@Component({
  selector: 'app-opkraevning-udestaaende-detail',
  imports: [
    CprNummerPipe,
    DatePipe,
    DecimalPipe,
    FormsModule,
    ScrollPanel,
    Button,
    InputNumber,
    LetDatePickerComponent,
    Dialog,
    Textarea,
    TableModule,
    TableRowDirective,
    Panel,
  ],
  templateUrl: './opkraevning-udestaaende-detail.component.html',
  styleUrl: './opkraevning-udestaaende-detail.component.css',
})
export class OpkraevningUdestaaendeDetailComponent {
  opkraevningUdestaaendeService = inject(OpkraevningUdestaaendeService);
  authService = inject(AuthService);
  retBeloeb = signal(false);
  retPeriode = signal(false);
  visAngivSomBehandlet = signal(false);
  visTilknytOpkraevning = signal(false);
  nytBeloeb = signal<number | undefined>(undefined);
  nyPeriodeFra = signal<string | undefined>(undefined);
  nyPeriodeTil = signal<string | undefined>(undefined);
  kommentar = signal<string | undefined>(undefined);
  muligeRisikoOpkraevninger = signal<RisikoOpkraevning[]>([]);
  valgtOpkraevning = signal<RisikoOpkraevning | undefined>(undefined);

  constructor() {
    effect(() => {
      const opkraevning =
        this.opkraevningUdestaaendeService.tilknyttetOpkraevning();
      if (opkraevning) {
        untracked(() => {
          this.nytBeloeb.set(opkraevning.opkraevningsBeloeb);
          this.nyPeriodeFra.set(opkraevning.periodeFra);
          this.nyPeriodeTil.set(opkraevning.periodeTil);
        });
      }
    });
  }

  allowRettelse = computed(() => {
    return (
      this.authService.hasRettighed(
        RettighedsType.AdminRisikoopkraevningSkriv
      ) && !this.opkraevningUdestaaendeService.selectedUdestaaende()?.behandlet
    );
  });

  allowGemKommentar = computed(() => (this.kommentar()?.length || 0) >= 5);

  async gemBeloeb() {
    await this.opkraevningUdestaaendeService.gemBeloeb(this.nytBeloeb() || 0);
    this.retBeloeb.set(false);
  }

  async gemPeriode() {
    await this.opkraevningUdestaaendeService.gemPeriode(
      this.nyPeriodeFra(),
      this.nyPeriodeTil()
    );
    this.retPeriode.set(false);
  }

  async angivSomBehandlet() {
    await this.opkraevningUdestaaendeService.angivSomBehandlet(
      this.kommentar()!
    );
    this.visAngivSomBehandlet.set(false);
  }

  async visTilknyt() {
    this.valgtOpkraevning.set(undefined);
    const liste =
      await this.opkraevningUdestaaendeService.hentMuligeOpkraevninger();
    this.muligeRisikoOpkraevninger.set(liste);
    this.visTilknytOpkraevning.set(true);
  }

  async gemTilknytning() {
    await this.opkraevningUdestaaendeService.tilknytOpkraevning(
      this.valgtOpkraevning()!.id
    );
    this.visTilknytOpkraevning.set(false);
    await this.opkraevningUdestaaendeService.reloadUdestaaende();
  }
}
